/**
 * The capability map and the pure logic over it.
 *
 * The person picks what they want Magnis to DO. Each choice implies module
 * packages and, for the ones that fetch data, a source to connect — they never
 * pick a "module" or a "source" by name. This is the single table those
 * mappings live in; screens, the installer and Settings all read it.
 */

export interface Capability {
  readonly id: string;
  readonly title: string;
  /** Modules this capability directly needs. */
  readonly modules: readonly string[];
  /** Source that feeds it, if any. */
  readonly source?: string;
  /** Whether it brings people into the graph (pulls in `contacts`). */
  readonly people?: boolean;
  /** Costs nothing and needs no account — safe to preselect. */
  readonly local?: boolean;
}

export const CAPABILITIES: readonly Capability[] = [
  { id: "email", title: "Email", modules: ["email"], source: "google", people: true },
  { id: "meetings", title: "Meetings", modules: ["meetings"], source: "google", people: true },
  { id: "telegram", title: "Telegram", modules: ["telegram"], source: "telegram", people: true },
  { id: "x", title: "X", modules: ["x"], source: "x", people: true },
  { id: "linkedin", title: "LinkedIn", modules: ["linkedin"], source: "anysite", people: true },
  { id: "notes", title: "Notes", modules: ["notes"], local: true },
  { id: "projects", title: "Projects", modules: ["projects"], local: true },
  { id: "companies", title: "Companies", modules: ["companies"], local: true },
  { id: "files", title: "Files", modules: ["file"], local: true },
];

/**
 * Install order, dependency-safe. The declared HARD edges (rpc_calls +
 * creates_schemas + requires_schemas, per the backend's deps.rs) are:
 *   contacts → email.ensure_address, companies.company
 *   meetings → email.ensure_address, contacts.person
 *   email    → triggers.create
 * so triggers precedes email, email precedes contacts, and contacts precedes
 * everything that reads people.
 */
export const INSTALL_ORDER: readonly string[] = [
  "triggers",
  "companies",
  "email",
  "contacts",
  "file",
  "meetings",
  "notes",
  "projects",
  "telegram",
  "x",
  "linkedin",
];

/** Always installed, never offered as a choice (`tier = "system"`). */
export const ALWAYS: readonly string[] = ["triggers"];

/**
 * Modules that another module hard-depends on. `contacts` calling
 * `email.ensure_address` is why picking Telegram alone still needs `email`:
 * installing contacts without it leaves an unmet hard requirement.
 */
const HARD_DEPS: Readonly<Record<string, readonly string[]>> = {
  contacts: ["email", "companies"],
  meetings: ["email", "contacts"],
  email: ["triggers"],
  telegram: ["contacts", "triggers"],
  x: ["contacts"],
  linkedin: ["contacts"],
};

/** Resolve the transitive hard-dependency closure of a module set. */
function closure(seed: readonly string[]): Set<string> {
  const out = new Set<string>(seed);
  const queue = [...seed];
  while (queue.length > 0) {
    const next = queue.shift();
    if (next === undefined) break;
    for (const dep of HARD_DEPS[next] ?? []) {
      if (!out.has(dep)) {
        out.add(dep);
        queue.push(dep);
      }
    }
  }
  return out;
}

export interface Resolution {
  /** Modules to install, in dependency-safe order. */
  readonly modules: readonly string[];
  /** Sources to offer, deduped — Email + Meetings is ONE Google connection. */
  readonly sources: readonly string[];
}

/**
 * Turn picked capability ids into what actually has to happen.
 *
 * `contacts` is conditional: added exactly once when any people-bearing card is
 * picked, and not at all otherwise.
 */
export function resolve(picked: readonly string[]): Resolution {
  const chosen = CAPABILITIES.filter((c) => picked.includes(c.id));

  const seed = new Set<string>(ALWAYS);
  for (const c of chosen) {
    for (const m of c.modules) seed.add(m);
    if (c.people === true) seed.add("contacts");
  }

  const wanted = closure([...seed]);
  const modules = INSTALL_ORDER.filter((m) => wanted.has(m));

  const sources: string[] = [];
  for (const c of chosen) {
    if (c.source !== undefined && !sources.includes(c.source)) sources.push(c.source);
  }

  return { modules, sources };
}

/** The capabilities preselected on first paint: everything that costs nothing. */
export function defaultPicks(): readonly string[] {
  return CAPABILITIES.filter((c) => c.local === true).map((c) => c.id);
}

// ── The step sequence ────────────────────────────────────────────────────────

export type Step =
  | "welcome"
  | "modules"
  | "sources"
  | "agent"
  | "search"
  | "installing"
  | "done";

/**
 * The screens that carry "Step N of M". Welcome is an introduction and the
 * closing screens report rather than ask, so neither is numbered — announcing
 * "Step 1 of 4" on the welcome screen and again on the first real question
 * made two different screens claim to be the same step.
 */
export const NUMBERED_STEPS: readonly Step[] = ["modules", "sources", "agent", "search"];

const ALL_STEPS: readonly Step[] = [
  "welcome",
  "modules",
  "sources",
  "agent",
  "search",
  "installing",
  "done",
];

/**
 * The screens THIS run actually visits.
 *
 * `sources` only exists when something the person picked needs an account.
 * Deriving the numbering from a fixed list instead made the counter jump from
 * 1 straight to 3.
 */
export function stepSequence(hasSources: boolean): readonly Step[] {
  return ALL_STEPS.filter((s) => s !== "sources" || hasSources);
}

/** Position of `step` among the numbered screens of this run, or null. */
export function stepNumber(
  step: Step,
  hasSources: boolean,
): { readonly index: number; readonly total: number } | null {
  const numbered = NUMBERED_STEPS.filter((s) => s !== "sources" || hasSources);
  const index = numbered.indexOf(step);
  if (index === -1) return null;
  return { index: index + 1, total: numbered.length };
}

function neighbour(step: Step, hasSources: boolean, delta: 1 | -1): Step {
  const seq = stepSequence(hasSources);
  const at = seq.indexOf(step);
  if (at === -1) return "welcome";
  const target = seq[Math.min(seq.length - 1, Math.max(0, at + delta))];
  return target ?? "welcome";
}

export function nextStep(step: Step, hasSources: boolean): Step {
  return neighbour(step, hasSources, 1);
}

export function prevStep(step: Step, hasSources: boolean): Step {
  return neighbour(step, hasSources, -1);
}

/**
 * Where to pick up from a persisted `current_step`.
 *
 * Quitting mid-wizard used to restart at Welcome and re-run every install,
 * because the stored step was written but never read back. A value this run
 * does not visit — "sources" after the person unpicked everything needing an
 * account — resolves to a screen that exists rather than stranding them on a
 * blank one, and anything unrecognised starts over.
 */
export function resumeStep(
  stored: string | null | undefined,
  hasSources: boolean,
): Step {
  if (stored === null || stored === undefined) return "welcome";
  const seq = stepSequence(hasSources);
  if (seq.includes(stored as Step)) return stored as Step;
  // Known step, just not part of this run: fall to the screen before it.
  const atAll = ALL_STEPS.indexOf(stored as Step);
  if (atAll === -1) return "welcome";
  for (let i = atAll - 1; i >= 0; i--) {
    const candidate = ALL_STEPS[i];
    if (candidate !== undefined && seq.includes(candidate)) return candidate;
  }
  return "welcome";
}
